import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink, Linkedin, Globe, FileText, Award, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function ExpertProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expert, setExpert] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchExpert() {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from("consultation_experts")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) {
        setError("Failed to load expert profile.");
      } else if (!data) {
        setError("Expert not found.");
      } else {
        setExpert(data);
      }
      setLoading(false);
    }
    if (id) fetchExpert();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-grandpal-green" />
        <span className="ml-2 text-gray-600">Loading expert profile...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <section className="py-12 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-3xl">
        <Card>
          <CardHeader className="flex flex-col items-center gap-4">
            <img
              src={expert.profile_image_url || "/lovable-uploads/e7b5d364-ad74-4341-aa95-ba4e6d7d1836.png"}
              alt={expert.full_name}
              className="w-28 h-28 rounded-full object-cover border-4 border-grandpal-green shadow"
            />
            <CardTitle className="text-2xl font-bold text-gray-900 text-center">{expert.full_name}</CardTitle>
            <div className="text-grandpal-green-dark font-medium text-lg">{expert.professional_title}</div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Calendar className="h-4 w-4" />
              {expert.years_experience}+ years experience
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {expert.specializations?.map((spec: string, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-xs">{spec}</Badge>
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">About</h3>
              <p className="text-gray-700 whitespace-pre-line">{expert.bio || expert.short_description}</p>
            </div>
            {expert.certifications && expert.certifications.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2"><Award className="h-4 w-4" />Certifications</h3>
                <ul className="list-disc list-inside text-gray-700">
                  {expert.certifications.map((cert: string, idx: number) => (
                    <li key={idx}>{cert}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col md:flex-row gap-4">
              {expert.linkedin_url && (
                <Button asChild variant="outline" className="flex-1">
                  <a href={expert.linkedin_url} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="h-4 w-4 mr-2" />LinkedIn
                  </a>
                </Button>
              )}
              {expert.website_url && (
                <Button asChild variant="outline" className="flex-1">
                  <a href={expert.website_url} target="_blank" rel="noopener noreferrer">
                    <Globe className="h-4 w-4 mr-2" />Website
                  </a>
                </Button>
              )}
              {expert.cv_file_url && (
                <Button asChild variant="outline" className="flex-1">
                  <a href={expert.cv_file_url} target="_blank" rel="noopener noreferrer">
                    <FileText className="h-4 w-4 mr-2" />CV/Resume
                  </a>
                </Button>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-800">Hourly Rate:</span>
                <span className="text-xl font-bold text-grandpal-green">€{expert.hourly_rate}</span>
              </div>
              {expert.google_calendar_link && (
                <Button asChild className="w-full bg-grandpal-green hover:bg-grandpal-green/90 text-white mt-2">
                  <a href={expert.google_calendar_link} target="_blank" rel="noopener noreferrer">
                    Book Consultation <ExternalLink className="h-4 w-4 ml-2" />
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
} 